import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { EnrollService, EnrollResponse } from '../../services/enroll.service';
import { KeycloakService } from 'keycloak-angular';
import { Router } from '@angular/router';

@Component({
  selector: 'app-enroll',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
  <div class="enroll-wrap">
    <!-- Overlay de carga centrado -->
    <div class="loading-overlay" *ngIf="loading" aria-live="polite" aria-busy="true">
      <div class="loader">
        <div class="spinner"></div>
        <div class="loading-text">Procesando…</div>
      </div>
    </div>
    <div class="card">
      <h2 class="title">Enrolamiento de negocio</h2>
      <p class="desc">Completa los campos requeridos. Esta operación es pública y no requiere autenticación.</p>

      <div class="mock-actions">
        <button type="button" class="btn" (click)="applyMockData()">Generar datos de prueba</button>
        <span class="hint">Temporal: valores únicos para evitar conflictos (409).</span>
      </div>

      <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
        <div class="grid">
          <h3 class="section-title">Negocio</h3>
          <label>
            RUT / Tax ID*
            <input formControlName="taxId" placeholder="12345678-9" />
          </label>
          <label>
            Nombre*
            <input formControlName="name" placeholder="Panadería Ejemplo" />
          </label>
          <label>
            País (ID)*
            <input type="number" formControlName="countryId" />
          </label>
          <label>
            Dirección*
            <input formControlName="address" />
          </label>
          <label>
            Comuna (ID)*
            <input type="number" formControlName="comunneId" />
          </label>
          <label>
            Email de contacto
            <input type="email" formControlName="email" />
          </label>
          <label>
            Teléfono
            <input formControlName="phoneNumber" />
          </label>

          <h3 class="section-title">Administrador</h3>

          <label>
            Email*
            <input type="email" formControlName="adminEmail" placeholder="administrador@empresa.cl" />
          </label>
          <label>
            Nombre*
            <input formControlName="adminFirstName" placeholder="Nombre administrador" />
          </label>
          <label>
            Apellido*
            <input formControlName="adminLastName" placeholder="Apellido administrador" />
          </label>
          <label>
            Contraseña* (mínimo 8)
            <input type="password" formControlName="adminPassword" placeholder="Contraseña administrador" />
          </label>
        </div>

        <div class="actions">
          <button class="btn primary" type="submit" [disabled]="loading">{{ loading ? 'Procesando…' : 'Crear negocio' }}</button>
        </div>
      </form>

      <p class="error" *ngIf="error">{{ error }}</p>
    </div>
  </div>
  `,
  styles: [
    `
    .enroll-wrap{display:flex;align-items:center;justify-content:center;min-height:70vh;padding:24px}
    .card{max-width:780px;width:100%;background:#fff;border-radius:12px;box-shadow:0 6px 16px rgba(0,0,0,.08);padding:24px}
    .title{margin:0 0 8px 0;font-size:22px;font-weight:600}
    .desc{margin:0 16px 16px 0;color:#6b7280;font-size:14px}
    .mock-actions{display:flex;align-items:center;gap:10px;margin:4px 0 12px 0}
    .hint{color:#6b7280;font-size:12px}
    form .grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    label{display:flex;flex-direction:column;font-size:12px;color:#374151}
    input{margin-top:6px;border:1px solid #d1d5db;border-radius:8px;padding:10px 12px;font-size:14px}
    .section-title{grid-column:1/-1;margin:8px 0 4px 0;font-size:16px;font-weight:600;color:#111827}
    .actions{margin-top:16px;display:flex;gap:12px}
    .btn{appearance:none;border:none;border-radius:8px;padding:10px 16px;background:#f3f4f6;color:#111827;font-weight:500;cursor:pointer}
    .btn.primary{background:#f26600;color:#fff}
    .btn:hover{filter:brightness(.98)}
    .error{margin-top:12px;color:#b91c1c;background:#fee2e2;border:1px solid #fecaca;padding:10px;border-radius:8px}
    .success{margin-top:12px;color:#065f46;background:#d1fae5;border:1px solid #a7f3d0;padding:10px;border-radius:8px}
    code{background:#f3f4f6;padding:0 4px;border-radius:4px}
    /* Overlay de carga */
    .loading-overlay{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.65);backdrop-filter:blur(2px);z-index:1000}
    .loader{display:flex;flex-direction:column;align-items:center;gap:8px;padding:16px 20px;border-radius:12px;background:#ffffff;box-shadow:0 6px 16px rgba(0,0,0,.08)}
    .spinner{width:36px;height:36px;border-radius:9999px;border:3px solid #e5e7eb;border-top-color:#f26600;animation:spin .8s linear infinite}
    .loading-text{color:#374151;font-weight:600}
    @keyframes spin{to{transform:rotate(360deg)}}
    `
  ]
})
export class EnrollComponent implements OnInit {
  loading = false;
  success?: { businessUuid: string; adminUserId: string };
  error?: string;
  private draftKey = 'enrollFormDraft';

  form = this.fb.group({
    taxId: ['', [Validators.required]],
    name: ['', [Validators.required]],
    countryId: [56, [Validators.required]],
    address: ['', [Validators.required]],
    comunneId: [1, [Validators.required]],
    email: [''],
    phoneNumber: [''],
    adminEmail: ['', [Validators.required, Validators.email]],
    adminFirstName: ['', [Validators.required]],
    adminLastName: ['', [Validators.required]],
    adminPassword: ['', [Validators.required, Validators.minLength(8)]],
  });

  constructor(private fb: FormBuilder, private enrollSvc: EnrollService, private kc: KeycloakService, private router: Router) {}

  ngOnInit(): void {
    try {
      const saved = localStorage.getItem(this.draftKey);
      if (saved) {
        const data = JSON.parse(saved);
        this.form.patchValue(data);
        return;
      }
    } catch {}
    // Si no hay borrador guardado, genera mocks por defecto
    this.applyMockData();
  }

  // Genera un set de datos mock coherentes y únicos para pruebas
  private generateMock() {
    const rand = Math.floor(1000 + Math.random() * 9000);
    const ts = Date.now();
    const taxNumeric = (10000000 + Math.floor(Math.random() * 89999999)).toString();
    const dv = (Math.floor(Math.random() * 9)).toString();
    return {
      taxId: `${taxNumeric}-${dv}`,
      name: `Panadería Ejemplo ${rand}`,
      countryId: 56,
      address: `Av. Siempre Viva ${rand}`,
      comunneId: 1,
      email: `contacto+${rand}@panaderia.cl`,
      phoneNumber: `+5699${Math.floor(1000000 + Math.random() * 8999999)}`,
      adminEmail: `admin+${ts}@panaderia.cl`, // único para evitar 409
      adminFirstName: 'Ana',
      adminLastName: 'Pérez',
      adminPassword: 'Cambiar123!'
    };
  }

  // Aplica los datos mock al formulario y limpia cualquier borrador previo
  applyMockData() {
    try { localStorage.removeItem(this.draftKey); } catch {}
    this.form.patchValue(this.generateMock());
  }

  submit() {
    this.error = undefined;
    this.success = undefined;

    if (this.form.invalid) {
      this.error = 'Completa todos los campos requeridos.';
      return;
    }
    this.loading = true;

    const payload = this.form.value as any;
    this.enrollSvc.enroll(payload).subscribe({
      next: (res: EnrollResponse) => {
        this.loading = false;
        try { localStorage.removeItem(this.draftKey); } catch {}
        // Redirige a vista de éxito con resumen y mensaje de validación
        const businessUuid = (res?.business?.uuid || '').toString();
        this.router.navigate(['/enroll/success'], { queryParams: { email: payload.adminEmail, businessUuid } });
      },
      error: (e: Error) => {
        this.loading = false;
        this.error = e?.message || 'Error al enrolar';
        try { localStorage.setItem(this.draftKey, JSON.stringify(this.form.value)); } catch {}
      }
    });
  }

  async loginAdmin() {
    // Redirigir al login personalizado
    this.router.navigateByUrl('/login');
  }
}